import { Input, Table, TableColumnsType, Typography, Tag, Select, DatePicker, Button, Space, Card, Statistic, Row, Col, Modal, Descriptions } from "antd";
import moment from "moment";
import { SearchOutlined, DownloadOutlined, InfoCircleOutlined, UserOutlined, WarningOutlined, HistoryOutlined, DollarOutlined } from "@ant-design/icons";
import NextIcon from "components/Icons/NextIcon";
import PreviousIcon from "components/Icons/PreviousIcon";
import useActivityLog from "hooks/useActivityLog";
import { IActivityLog } from "models/activityLog";
import { IAccount } from "models/account";
import { useState } from "react";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

export default function ActivityLog() {
  const {
    data: { data, loading, page, per_page, search, total },
    stats,
    handlePageChange,
    handleSearch,
    fetchActivityLog,
    exportLogs,
  } = useActivityLog();

  const [filters, setFilters] = useState<{
    severity: string | undefined;
    action_category: string | undefined;
    account_role: string | undefined;
    dateRange: [any, any] | null;
  }>({
    severity: undefined,
    action_category: undefined,
    account_role: undefined,
    dateRange: null,
  });

  const [detailModal, setDetailModal] = useState<{ visible: boolean; log: IActivityLog | null }>({
    visible: false,
    log: null,
  });

  const onFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    const apiFilters: any = {
      severity: newFilters.severity,
      action_category: newFilters.action_category,
      account_role: newFilters.account_role,
    };

    if (newFilters.dateRange) {
      apiFilters.from_date = newFilters.dateRange[0]?.format("YYYY-MM-DD");
      apiFilters.to_date = newFilters.dateRange[1]?.format("YYYY-MM-DD");
    }

    fetchActivityLog(apiFilters);
  };

  const columns: TableColumnsType<IActivityLog> = [
    {
      title: "Timestamp",
      dataIndex: "created_at",
      width: 180,
      render: (createdAt) => (
        <div>
          <p className="m-0 font-medium">{moment(createdAt).format("MMM DD, YYYY")}</p>
          <p className="m-0 text-gray-400 text-xs">{moment(createdAt).format("HH:mm:ss")}</p>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "action_category",
      width: 120,
      render: (category: string) => {
        const colors = {
          financial: "green",
          security: "red",
          system: "blue",
          business: "purple",
          driver: "cyan",
          trip: "orange",
        };
        return <Tag color={colors[category as keyof typeof colors] || "default"} className="capitalize">{category || "system"}</Tag>;
      },
    },
    {
      title: "Account",
      dataIndex: "account",
      width: 200,
      render: (account: IAccount | null, record) => (
        <div>
          <p className="m-0 font-semibold">{account ? `${account.first_name} ${account.last_name}` : "System"}</p>
          <Tag color="cyan" className="text-[10px] uppercase m-0">{record.account_role || "system"}</Tag>
        </div>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      width: 100,
      render: (severity: string) => {
        const colors = {
          low: "blue",
          medium: "orange",
          high: "volcano",
          critical: "red",
        };
        return <Tag color={colors[severity as keyof typeof colors] || "default"} className="capitalize">{severity}</Tag>;
      },
    },
    {
      title: "Action & Description",
      dataIndex: "action",
      render: (action, record) => (
        <div>
          <p className="font-bold text-blue-600 m-0 capitalize">{action.replace(/_/g, " ")}</p>
          <p className="text-gray-500 text-sm m-0">{record.description}</p>
        </div>
      ),
    },
    {
      title: "Network & Device",
      width: 200,
      render: (_, record) => (
        <div className="text-xs">
          <p className="m-0 text-gray-400"><strong>IP:</strong> {record.ip_address}</p>
          <p className="m-0 text-gray-400"><strong>OS:</strong> {record.device_info?.os || "Unknown"}</p>
          <p className="m-0 text-gray-400 font-italic">{record.location || ""}</p>
        </div>
      ),
    },
    {
      title: "Details",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<InfoCircleOutlined />}
          onClick={() => setDetailModal({ visible: true, log: record })}
        />
      ),
    },
  ];

  return (
    <div className="w-full p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Enhanced Activity Logs</Title>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => exportLogs("csv")}
            type="primary"
          >
            Export CSV
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => exportLogs("excel")}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      {stats && (
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={6}>
            <Card className="shadow-sm border-l-4 border-blue-500">
              <Statistic title="Total Logs" value={stats?.total_logs || 0} prefix={<HistoryOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="shadow-sm border-l-4 border-red-500">
              <Statistic title="Critical Events" value={stats?.severity_breakdown?.critical || 0} prefix={<WarningOutlined className="text-red-500" />} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="shadow-sm border-l-4 border-green-500">
              <Statistic title="Financial Actions" value={stats?.action_breakdown?.wallet_deducted || 0} prefix={<DollarOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="shadow-sm border-l-4 border-purple-500">
              <Statistic title="Last 24 Hours" value={stats?.recent_activity?.last_24_hours || 0} />
            </Card>
          </Col>
        </Row>
      )}

      <Card className="mb-6 shadow-sm">
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search actions, accounts..."
              onPressEnter={(e) => handleSearch(e.currentTarget.value)}
              allowClear
              className="rounded-lg"
            />
          </Col>
          <Col xs={12} md={3}>
            <Select
              placeholder="Category"
              className="w-full"
              allowClear
              onChange={(v) => onFilterChange("action_category", v)}
            >
              <Select.Option value="system">System</Select.Option>
              <Select.Option value="financial">Financial</Select.Option>
              <Select.Option value="security">Security</Select.Option>
              <Select.Option value="business">Business</Select.Option>
              <Select.Option value="driver">Driver</Select.Option>
              <Select.Option value="trip">Trip</Select.Option>
            </Select>
          </Col>
          <Col xs={12} md={3}>
            <Select
              placeholder="Severity"
              className="w-full"
              allowClear
              onChange={(v) => onFilterChange("severity", v)}
            >
              <Select.Option value="low">Low</Select.Option>
              <Select.Option value="medium">Medium</Select.Option>
              <Select.Option value="high">High</Select.Option>
              <Select.Option value="critical">Critical</Select.Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Account Role"
              className="w-full"
              allowClear
              onChange={(v) => onFilterChange("account_role", v)}
            >
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="driver">Driver</Select.Option>
              <Select.Option value="passenger">Passenger</Select.Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <RangePicker
              className="w-full"
              onChange={(v) => onFilterChange("dateRange", v)}
            />
          </Col>
        </Row>
      </Card>

      <div className="w-full mt-4">
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          onChange={handlePageChange}
          pagination={{
            total,
            pageSize: per_page,
            current: page,
            showSizeChanger: true,
            nextIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <NextIcon />
              </span>
            ),
            prevIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <PreviousIcon />
              </span>
            ),
          }}
          className="shadow-sm rounded-lg overflow-hidden"
        />
      </div>

      <Modal
        title={`Activity Detail: ${detailModal.log?.action.replace(/_/g, " ").toUpperCase()}`}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, log: null })}
        footer={null}
        width={800}
      >
        {detailModal.log && (
          <div className="flex flex-col gap-y-6">
            <Descriptions bordered layout="vertical" column={3} size="small">
              <Descriptions.Item label="Severity">
                <Tag color={detailModal.log.severity === 'critical' ? 'red' : 'blue'}>
                  {detailModal.log.severity.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="IP Address">{detailModal.log.ip_address}</Descriptions.Item>
              <Descriptions.Item label="Location">{detailModal.log.location || 'Unknown'}</Descriptions.Item>
              <Descriptions.Item label="Browser">{detailModal.log.device_info?.browser || 'Unknown'}</Descriptions.Item>
              <Descriptions.Item label="OS">{detailModal.log.device_info?.os || 'Unknown'}</Descriptions.Item>
              <Descriptions.Item label="Session ID">{detailModal.log.session_id || 'N/A'}</Descriptions.Item>
            </Descriptions>

            <div className="bg-gray-50 p-4 rounded-md">
              <Title level={5}>Description</Title>
              <Text>{detailModal.log.description}</Text>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>Before State</Title>
                <pre className="bg-gray-100 p-3 rounded text-xs min-h-[100px] overflow-auto">
                  {JSON.stringify(detailModal.log.before_state, null, 2) || "No state information"}
                </pre>
              </Col>
              <Col span={12}>
                <Title level={5}>After State</Title>
                <pre className="bg-gray-100 p-3 rounded text-xs min-h-[100px] overflow-auto">
                  {JSON.stringify(detailModal.log.after_state, null, 2) || "No state information"}
                </pre>
              </Col>
            </Row>

            <div className="bg-blue-50 p-4 rounded-md">
              <Title level={5}>HTTP Metadata</Title>
              <Text type="secondary" className="block"><strong>Path:</strong> {detailModal.log.path}</Text>
              <Text type="secondary" className="block"><strong>Method:</strong> {detailModal.log.method}</Text>
              <Title level={5} className="mt-4">Request Body</Title>
              <pre className="bg-white p-2 border rounded text-xs">
                {JSON.stringify(detailModal.log.body, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
